import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { reportClientError } from "@/lib/analytics";

// Catches any render/runtime error in the page content so the app shows a branded
// fallback instead of a blank black screen. Auto-resets when the route changes
// (parent passes a changing `resetKey`).
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Something went wrong" };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    reportClientError(error?.message || "Render error", (error?.stack || "") + "\n" + (info?.componentStack || ""));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, message: "" });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="error-boundary-fallback" className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[#1A1A1D] border border-white/10 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h2 className="font-serif-lux text-2xl text-white mb-2">This page hit a snag</h2>
          <p className="text-sm text-white/50 mb-6">
            We couldn't display this content just now. Your data is safe — try reloading, or head back to your dashboard.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button data-testid="error-boundary-reload" onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 gold-btn rounded-lg px-5 py-2.5 text-sm">
              <RotateCcw className="w-4 h-4" /> Reload page
            </button>
            <button data-testid="error-boundary-home" onClick={() => window.location.assign("/")}
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm border border-white/10 text-white/70 hover:text-white hover:bg-white/5 transition-colors">
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
